import { NovuConfiguration, PreferenceDto, UpdatedPreferenceDto } from './types';

interface Configuration {
	origin: string;
	getAccessToken?: () => Promise<string>;
}

const configuration: Configuration = {
	origin: window.origin + '/io/notify',
};

export function configureNotifyHttp(configure: (current: Configuration) => void) {
	configure(configuration);
}

export async function createRequest(path: string, init: RequestInit = {}) {
	let url = path;
	init.headers = { ...init.headers, 'X-BFF-CSRF': 'true' };
	if (!(url.startsWith('http') || url.startsWith('//')) && configuration.origin) {
		const normalizedBase = configuration.origin.endsWith('/')
			? configuration.origin.slice(0, -1)
			: configuration.origin;
		const normalizedPath = !url.startsWith('/') ? '/' + url : url;
		url = normalizedBase + normalizedPath;
	}
	if (!init.headers?.hasOwnProperty('authorization') && configuration.getAccessToken) {
		const accessToken = await configuration.getAccessToken();
		const headersWithAuth: HeadersInit = {
			...init.headers,
			authorization: `Bearer ${accessToken}`,
		};
		init.headers = headersWithAuth;
	}
	return new Request(url, init);
}

export const NotifyHttp = {
	async getGlobalPreferences() {
		const endpoint = new URL(`${configuration.origin}/api/v1/Preferences`);
		const request = await createRequest(endpoint.toString(), { method: 'GET' });
		const response = await fetch(request);

		if (response.status !== 200) {
			throw new Error(
				`could not get notification preferences - response: ${response.status}, ${response.statusText}`
			);
		}

		const responseText = await response.text();
		const responseJSON = JSON.parse(responseText, dateReviver);

		return responseJSON as PreferenceDto;
	},

	async updateGlobalPreferences(payload: PreferenceDto) {
		const endpoint = new URL(`${configuration.origin}/api/v1/Preferences`);
		const request = await createRequest(endpoint.toString(), {
			method: 'POST',
			body: JSON.stringify(payload),
			headers: { 'content-type': 'application/json' },
		});

		const response = await fetch(request);
		if (response.status !== 200) {
			throw new Error('could not update user preference');
		}

		const responseText = await response.text();
		return JSON.parse(responseText, dateReviver) as UpdatedPreferenceDto;
	},

	async getHmacToken() {
		const endpoint = new URL(`${configuration.origin}/api/v1/Hmac`);
		const request = await createRequest(endpoint.toString(), { method: 'GET' });
		const response = await fetch(request);

		if (response.status !== 200) {
			throw new Error(
				`could not get messages - response: ${response.status}, ${response.statusText}`
			);
		}

		const responseText = await response.text();

		return responseText;
	},

	async getNovuConfiguration() {
		const endpoint = new URL(
			`${configuration.origin}/api/v1/configuration/novu-with-hmac`
		);
		const request = await createRequest(endpoint.toString(), { method: 'GET' });
		const response = await fetch(request);

		if (response.status !== 200) {
			throw new Error(
				`could not get novu configuration - response: ${response.status}, ${response.statusText}`
			);
		}

		const responseText = await response.text();
		return JSON.parse(responseText) as NovuConfiguration;
	},
};

function dateReviver(_key: string, value: string) {
	if (typeof value === 'string') {
		if (
			/^\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)$/.test(
				value
			)
		) {
			return new Date(value);
		}
	}
	return value;
}
